'use client';

import { useToast } from '@/hooks/use-toast';
import { DOMAIN, SlovLexUrl } from '@/lib/config';
import { documentSlug } from '@/lib/utils/documentMetadata';
import { useLocale, useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import { useRef } from 'react';
import styles from './html-link-handler.module.css';
import { LegislationInstrumentPopupMenu } from './legislation-instrument-popup-menu';

interface ElementOptionsMenuProps {
  /** Static element ID (legacy callers) */
  elementId?: string;
  /** Getter that reads the current element ID from a ref at click time (avoids re-renders) */
  getElementId?: () => string;
  activeElementPosition: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  toolbarHeight?: number | undefined;
  iri?: string | undefined;
  documentId?: number | undefined;
}

/**
 * Convert IRI format to Slov-lex URL path format
 * IRI: eli/sk/zakon/2024/39/vyhlasene/20240224
 * Slov-lex: SK/ZZ/2024/39/20240224
 */
function iriToSlovLexPath(iri: string): string {
  // Remove leading slash if present
  const cleanIri = iri.startsWith('/') ? iri.substring(1) : iri;

  // Parse IRI: eli/sk/{type}/{year}/{number}/{version}/{date}
  const parts = cleanIri.split('/');
  if (parts.length < 6 || parts[0] !== 'eli' || parts[1] !== 'sk') {
    // Return as-is if not in expected format
    return cleanIri;
  }

  // parts[2] = type (zakon, nariadenie, etc.)
  // parts[3] = year
  // parts[4] = number
  // parts[5] = version (vyhlasene, etc.) - skip this
  // parts[6] = date (if present)

  const year = parts[3];
  const number = parts[4];
  const date = parts[6] || parts[5]; // Use date if present, otherwise version

  // Slov-lex format: SK/ZZ/{year}/{number}/{date}
  return `SK/ZZ/${year}/${number}/${date}`;
}

export function ElementOptionsMenu({
  elementId: elementIdProp,
  getElementId,
  activeElementPosition,
  iri: iriProp,
  documentId: documentIdProp,
}: ElementOptionsMenuProps) {
  const { x, y } = activeElementPosition;
  // Resolve element ID: prefer getter (ref-based, no re-render) over static prop
  const resolveElementId = () => getElementId?.() || elementIdProp || '';
  const params = useParams();
  // Support both route formats: /document/[...segments] and /document/[identifier]
  const segments = params['segments'] as string[] | undefined;
  const identifier = params['identifier'] as string | undefined;
  // Use prop first, then try segments, then try identifier (if it looks like an IRI)
  const iri =
    iriProp ||
    segments?.join('/') ||
    (identifier && !/^\d+$/.test(identifier) ? identifier : '') ||
    '';
  // For Lexomat, use documentId prop or identifier if it's numeric
  const documentId =
    documentIdProp ||
    (identifier && /^\d+$/.test(identifier) ? identifier : null);

  const menuRef = useRef<HTMLDivElement>(null);
  const locale = useLocale();
  const { toast } = useToast();
  const t = useTranslations('LegislationDetails.linkCopied');

  const handleCopyLinkLexomat = () => {
    const elementId = resolveElementId();
    // Lexomat uses document ID for the URL
    const url = documentId
      ? `${DOMAIN}/${locale}/${documentSlug(locale)}/${documentId}#${elementId}`
      : iri
        ? `${DOMAIN}/${locale}/${documentSlug(locale)}/${iri}#${elementId}`
        : `#${elementId}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast({
          title: t('title'),
          description: t('description'),
          variant: 'success',
        });
      })
      .catch(() => {
        toast({
          title: t('errorTitle'),
          description: t('errorDescription'),
          variant: 'destructive',
        });
      });
  };

  const handleCopyLinkSlovLex = () => {
    const elementId = resolveElementId();
    // Convert IRI to Slov-lex path format
    const slovLexPath = iri ? iriToSlovLexPath(iri) : '';
    const url = slovLexPath
      ? `${SlovLexUrl}/${slovLexPath}#${elementId}`
      : `#${elementId}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        toast({
          title: t('title'),
          description: t('description'),
          variant: 'success',
        });
      })
      .catch(() => {
        toast({
          title: t('errorTitle'),
          description: t('errorDescription'),
          variant: 'destructive',
        });
      });
  };

  return (
    <div
      ref={menuRef}
      className={styles['enhancedFloatingToolbar']}
      style={{
        left: `${x}px`,
        top: `${y}px`,
        transform: 'translateX(-100%)',
      }}
    >
      <LegislationInstrumentPopupMenu
        handleCopyLinkLexomat={handleCopyLinkLexomat}
        handleCopyLinkSlovLex={handleCopyLinkSlovLex}
        showSlovLex={!!iri}
      />
    </div>
  );
}
